import React from 'react';
import { motion } from 'framer-motion';
import { Lock, ShieldCheck, Key, FileText, BarChart3, BookOpen, PieChart, FileBarChart } from 'lucide-react';
interface InvestorSectionProps {
  onLoginClick: () => void;
  onRequestAllocation: () => void;
}
const resources = [{
  icon: <BarChart3 className="w-5 h-5 text-gold-400" data-id="element-116" />,
  title: 'Live Performance',
  description: 'Real-time NAV, P&L attribution, and benchmark comparisons across all 11 strategy clusters.'
}, {
  icon: <PieChart className="w-5 h-5 text-gold-400" data-id="element-117" />,
  title: 'Strategy Allocations',
  description: 'Current portfolio weights, rebalancing history, and position-level transparency.'
}, {
  icon: <BookOpen className="w-5 h-5 text-gold-400" data-id="element-118" />,
  title: 'Quarterly Letters',
  description: 'In-depth market commentary, strategy rationale, and forward-looking positioning from the CIO.'
}, {
  icon: <FileBarChart className="w-5 h-5 text-gold-400" data-id="element-119" />,
  title: 'Fund Documents',
  description: 'PPMs, subscription agreements, K-1s, audited financials, and compliance disclosures.'
}];
export function InvestorSection({
  onLoginClick,
  onRequestAllocation
}: InvestorSectionProps) {
  return <section id="lp-resources" className="py-24 md:py-32 bg-investor-glow relative overflow-hidden" data-id="element-120">
      {/* Crossing Diagonal Lines Background */}
      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none" data-id="element-121">
        <div className="absolute top-1/2 left-1/2 w-[200%] h-[1px] bg-gold-400/10 -translate-x-1/2 -translate-y-1/2 rotate-45" data-id="element-122"></div>
        <div className="absolute top-1/2 left-1/2 w-[200%] h-[1px] bg-gold-400/10 -translate-x-1/2 -translate-y-1/2 -rotate-45" data-id="element-123"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-124">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-125">
          {/* Left: Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8
        }} data-id="element-126">
            <h2 className="text-xs tracking-[0.25em] text-gold-400/70 uppercase mb-4 flex items-center gap-3" data-id="element-127">
              <div className="w-8 h-[1px] bg-gold-400/40" data-id="element-128"></div>
              Limited Partner Resources
            </h2>
            <h3 className="text-3xl md:text-5xl text-white leading-tight mb-6" data-id="element-129">
              <span className="font-light" data-id="element-130">Everything you need,</span>
              <br data-id="element-131" />
              <span className="font-bold" data-id="element-132">behind one login.</span>
            </h3>
            <p className="text-slate-400 text-lg leading-relaxed mb-8" data-id="element-133">
              Access detailed performance metrics, strategy allocations, and
              proprietary research reports. The portal is restricted to current
              limited partners and approved institutional allocators.
            </p>

            <button onClick={onLoginClick} className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-gold-400 text-slate-950 font-semibold tracking-wider hover:bg-gold-300 transition-all duration-300" data-id="element-134">
              <Lock className="w-5 h-5" data-id="element-135" />
              Access LP Portal
            </button>

            <div className="mt-6 text-sm text-slate-500 font-medium" data-id="element-136">
              Not a partner?{' '}
              <button onClick={onRequestAllocation} className="text-gold-400 hover:text-gold-300 underline underline-offset-4" data-id="element-137">
                Request Allocation Information
              </button>
            </div>
          </motion.div>

          {/* Right: Resource Cards */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-138">
            {resources.map((resource, index) => <div key={index} className="relative p-5 bg-base-950/60 border border-slate-800 backdrop-blur-sm group" data-id="element-139">
                <div className="p-2.5 bg-slate-900 border border-slate-800 inline-flex mb-4" data-id="element-140">
                  {resource.icon}
                </div>
                <h4 className="text-white font-bold mb-2 text-sm tracking-wide" data-id="element-141">
                  {resource.title}
                </h4>
                <p className="text-slate-500 text-xs leading-relaxed" data-id="element-142">
                  {resource.description}
                </p>
              </div>)}
          </motion.div>
        </div>

        {/* Security Badges */}
        <div className="flex flex-wrap justify-center items-center gap-6 mt-16 pt-8 border-t border-slate-800/50" data-id="element-143">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-wider" data-id="element-144">
            <ShieldCheck className="w-4 h-4 text-gold-400/70" data-id="element-145" />
            AES-256 Encryption
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-wider" data-id="element-146">
            <FileText className="w-4 h-4 text-gold-400/70" data-id="element-147" />
            SOC 2 Type II
          </div>
          <div className="flex items-center gap-2 text-slate-500 text-xs font-mono uppercase tracking-wider" data-id="element-148">
            <Key className="w-4 h-4 text-gold-400/70" data-id="element-149" />
            MFA Required
          </div>
        </div>
      </div>
    </section>;
}